// render-rules.ts — the "catalog-rules" renderer: the Game Rules INDEX (rules.php).
//
// Same JSON pipeline as the cards/table (PHP compiles a flat payload from icon_map() + the rule
// slugs; the browser holds it in memory and filters client-side, no jQuery) — but the page is a
// navigation index, not a catalog of one item_type. It's many ACCENT-COLOURED GROUPS (Glossary,
// Player Character creation, Skills, Magic, …), each a header over a grid of cards that link to
// static rule .php pages. The cards reuse the catalog card markup (image + bottom-right overlay
// button) but always carry a leading icon and link to a page rather than item.php?id=.
//
// The toolbar search box (data-ft-search-input-id) filters cards across ALL groups at once: a
// group's header + grid hide when every card in it is filtered out, and a single empty-state shows
// when nothing matches anywhere — mirrors the card renderer's section behaviour.

import type { CatalogListConfig, CatalogRulesResponse, RuleCard, RuleGroup } from './types';
import { resolveUrl, results, setStatus, showLoader, hideLoader, initTooltips } from './catalog-list';

// Bootstrap button colour suffixes the overlay button is allowed to use. The endpoint passes a
// bare suffix (e.g. 'warning'); anything off this list falls back to 'info' so a stray payload
// value can never inject an arbitrary class.
const BUTTON_VARIANTS = new Set(['info', 'warning', 'success', 'danger', 'primary', 'secondary', 'light', 'dark']);

// Non-navigating card actions: a card may fire a known client-side handler instead of linking to a
// page (player-tools "Roll Dice"). Whitelisted by key so the payload can't run arbitrary code.
const CARD_ACTIONS: Record<string, () => void> = {
    side_log: () => (window as unknown as { openSideLog?: () => void }).openSideLog?.(),
};

// One rule card: link → card → image → bottom-right overlay button (leading icon + name). Built
// with the DOM API (createElement + textContent) so the name is escaped intrinsically; the icon
// HTML is trusted icon_map() output from the endpoint (same contract as the table cells' html).
function renderRuleCard(card: RuleCard): HTMLAnchorElement {
    const link = document.createElement('a');
    link.className = 'text-decoration-none';
    // A card either navigates (url) or fires a whitelisted action on click (no href, so it behaves
    // as a button — matching the legacy player_tools.php "Roll Dice" <a role=button onclick=…>).
    const action = card.action ? CARD_ACTIONS[card.action] : undefined;
    if (action) {
        link.setAttribute('role', 'button');
        link.style.cursor = 'pointer';
        link.addEventListener('click', (e) => { e.preventDefault(); action(); });
    } else {
        link.href = card.url;
    }

    const cardEl = document.createElement('div');
    cardEl.className = 'card text-left mb-3';

    const img = document.createElement('img');
    img.src = card.picture_url || '';
    img.className = 'card-img';
    img.alt = card.name || 'Rule';
    img.loading = 'lazy';
    cardEl.appendChild(img);

    const overlay = document.createElement('div');
    overlay.className = 'card-img-overlay';
    const overlayInner = document.createElement('div');
    overlayInner.className = 'position-absolute bottom-0 end-0 me-3 mb-3';
    const btn = document.createElement('button');
    btn.type = 'button';
    const variant = card.variant && BUTTON_VARIANTS.has(card.variant) ? card.variant : 'info';
    btn.className = `btn btn-sm border btn-${variant} position-relative`;
    // Leading icon: resolve client-side from `icon_id` via iconMap() (pure-data payload); fall back to
    // a legacy pre-rendered `icon_html` for endpoints not yet migrated. NOTE: do NOT pass `false` as the
    // 4th arg — that makes iconMap() return a bare <img> with no tooltip wrapper; default keeps the
    // data-bs-toggle="tooltip" span so initTooltips() can wire the hover label.
    const cardIconHtml = card.icon_id ? iconMap(card.icon_id, 22, 22) : (card.icon_html || '');
    if (cardIconHtml) {
        const icon = document.createElement('span');
        icon.className = 'me-1';
        icon.innerHTML = cardIconHtml;
        btn.appendChild(icon);
    }
    btn.appendChild(document.createTextNode(card.name || 'Untitled'));
    overlayInner.appendChild(btn);
    overlay.appendChild(overlayInner);
    cardEl.appendChild(overlay);

    link.appendChild(cardEl);
    return link;
}

// A group's accent header: left bar + icon backing tinted with the group's accent colour (passed as
// the --ft-accent custom property the CSS reads). A group with no label renders a thin divider
// instead — it's a continuation of the preceding group (mirrors the original page's bare <hr>).
function renderGroupHeader(group: RuleGroup): HTMLElement {
    if (!group.label) {
        const hr = document.createElement('hr');
        hr.className = 'ft-rules-divider';
        return hr;
    }
    const header = document.createElement('div');
    header.className = 'ft-rules-group-title';
    header.style.setProperty('--ft-accent', group.accent || '#22d3ee');
    const groupIconHtml = group.icon_id ? iconMap(group.icon_id, 30, 30) : (group.icon_html || '');
    if (groupIconHtml) {
        const icon = document.createElement('span');
        icon.className = 'ft-rules-group-icon';
        icon.innerHTML = groupIconHtml;
        header.appendChild(icon);
    }
    const label = document.createElement('span');
    label.className = 'ft-rules-group-label';
    label.textContent = group.label;
    header.appendChild(label);
    return header;
}

interface BuiltCard { el: HTMLElement; text: string; }
interface BuiltGroup { header: HTMLElement; grid: HTMLElement; cards: BuiltCard[]; }

export function mountCatalogRules(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-rules] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read as text first so a stray PHP warning / HTML 500 surfaces a useful message rather
            // than an opaque JSON.parse error (same guard as render-table.ts).
            const raw = await res.text();
            let data: CatalogRulesResponse;
            try {
                data = JSON.parse(raw) as CatalogRulesResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(container, data, searchInput);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-rules] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load the rules index. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    container: HTMLElement,
    data: CatalogRulesResponse,
    searchInput: HTMLInputElement | null,
): void {
    container.innerHTML = '';

    const builtGroups: BuiltGroup[] = (data.groups || []).map((group, index) => {
        const header = renderGroupHeader(group);
        // A label-less group renders a thin divider marking a CONTINUATION of the preceding group.
        // The first group can't continue anything, so a leading label-less group emits nothing at
        // all (used by player_tools.php, a single-group index with no need for an accent header).
        // The element is still kept for filter bookkeeping; it's just never inserted into the DOM.
        if (group.label || index > 0) {
            container.appendChild(header);
        }

        const grid = document.createElement('div');
        grid.className = 'row m-0 row-cols-1 row-cols-md-2 row-cols-lg-3';
        container.appendChild(grid);

        // The card <a> is the DIRECT grid child (no .col wrapper) — matching the legacy rules.php
        // markup. This is load-bearing: css/style.css ships its own copy of Bootstrap's `.col`
        // (flex-grow:1; flex-basis:0) AFTER the Bootstrap CDN but WITHOUT the `.row-cols-*` rules,
        // so a `.col` wrapper would have its `.row-cols-lg-3 > * { width:33% }` clobbered and every
        // card would collapse to equal-width on one line. `.row-cols-lg-3 > a` has no such override.
        const cards: BuiltCard[] = (group.cards || []).map((card) => {
            const link = renderRuleCard(card);
            grid.appendChild(link);
            return { el: link, text: card.text || (card.name || '').toLowerCase() };
        });

        return { header, grid, cards };
    });

    // Single empty-state at the top of the reserved min-height space (shown only when a keyword
    // hides every card across every group).
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = 'No rules match your search.';
    emptyEl.style.display = 'none';
    container.appendChild(emptyEl);

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let totalVisible = 0;
        for (const group of builtGroups) {
            let visible = 0;
            for (const { el, text } of group.cards) {
                const show = !keyword || text.includes(keyword);
                el.style.display = show ? '' : 'none';
                if (show) visible++;
            }
            // Hide the header + grid when the group is fully filtered out. A continuation group's
            // divider (header) hides with it too, so no stray rule sits above empty space.
            group.header.style.display = visible > 0 ? '' : 'none';
            group.grid.style.display = visible > 0 ? '' : 'none';
            totalVisible += visible;
        }
        emptyEl.style.display = totalVisible === 0 ? '' : 'none';
    };

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as the cards/table).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();

    // Init Bootstrap tooltips on the freshly-built group headers / card icons (iconMap() output).
    initTooltips(container);
}
